import { CreditCard, X } from "../../../../components/icons";
import PlayerAvatar from "../../../../components/PlayerAvatar";
import { getPositionGroup } from "../../hooks/useTacticalPositions";
import { formatDisplayName } from "../../../../lib/nameFormat";
import type { Match, Player, StaffMember } from "../../types";
import type { TFunction } from "i18next";
import type { CardSelection } from "./QuickCardPanel";
import {
  getGroupMeta,
  sortPlayersByGroup,
  staffRoleLabel,
} from "./PlayerSelectorPanel";

interface CardSubjectModalProps {
  match: Match;
  cardType: CardSelection;
  selectedTeam: "home" | "away";
  onSelectTeam: (team: "home" | "away") => void;
  onFieldIds: { home: Set<string>; away: Set<string> };
  expelledPlayerIds?: Set<string>;
  disciplinaryStatusByPlayer?: Record<
    string,
    { yellowCount: number; red: boolean }
  >;
  globalClock: string;
  period: number;
  onSelectPlayer: (player: Player) => void;
  onSelectStaff: (staff: StaffMember) => void;
  onClose: () => void;
  t: TFunction<"logger">;
}

const CARD_TITLE_KEY: Record<CardSelection, string> = {
  Yellow: "cardSubjectModal.titleYellow",
  Red: "cardSubjectModal.titleRed",
  Cancelled: "cardSubjectModal.titleCancelled",
};

const CARD_TITLE_FALLBACK: Record<CardSelection, string> = {
  Yellow: "Yellow Card",
  Red: "Red Card",
  Cancelled: "Cancel Card",
};

const CARD_HEADER_GRADIENT: Record<CardSelection, string> = {
  Yellow: "from-amber-600 to-amber-700",
  Red: "from-red-700 to-red-800",
  Cancelled: "from-slate-600 to-slate-700",
};

export default function CardSubjectModal({
  match,
  cardType,
  selectedTeam,
  onSelectTeam,
  onFieldIds,
  expelledPlayerIds,
  disciplinaryStatusByPlayer,
  globalClock,
  period,
  onSelectPlayer,
  onSelectStaff,
  onClose,
  t,
}: CardSubjectModalProps) {
  const team = selectedTeam === "home" ? match.home_team : match.away_team;
  const teamOnField = onFieldIds[selectedTeam];
  const onFieldPlayers = sortPlayersByGroup(
    team.players.filter((p) => teamOnField.has(p.id)),
  );
  const benchPlayers = sortPlayersByGroup(
    team.players.filter((p) => !teamOnField.has(p.id)),
  );
  const staff = team.technical_staff ?? [];

  const isSubjectDisabled = (subjectId: string) =>
    (expelledPlayerIds?.has(subjectId) ?? false) && cardType !== "Cancelled";

  const renderPlayerCard = (player: Player) => {
    const group = getPositionGroup(player.position);
    const meta = getGroupMeta(group);
    const cardStatus = disciplinaryStatusByPlayer?.[player.id];
    const hasYellow = (cardStatus?.yellowCount ?? 0) > 0;
    const hasRed = Boolean(cardStatus?.red);
    const isExpelled = expelledPlayerIds?.has(player.id) ?? false;
    const isDisabled = isSubjectDisabled(player.id);
    return (
      <button
        key={player.id}
        type="button"
        data-testid={`player-card-${player.id}`}
        data-player-row="true"
        data-position-group={group}
        onClick={() => !isDisabled && onSelectPlayer(player)}
        disabled={isDisabled}
        className={`w-full px-3 py-2 rounded-lg border-l-4 text-left transition-all ${
          isDisabled
            ? "bg-slate-900 border-slate-800 opacity-50 cursor-not-allowed"
            : selectedTeam === "home"
              ? "bg-red-900/20 border-red-900/50 hover:border-red-500/50 hover:bg-red-900/30"
              : "bg-blue-900/20 border-blue-900/50 hover:border-blue-500/50 hover:bg-blue-900/30"
        } ${meta.border}`}
      >
        <div className="flex items-center gap-2.5">
          <PlayerAvatar player={player} size={20} />
          <span
            data-testid={`player-number-${player.id}`}
            className={`text-xs font-bold min-w-[2.25rem] ${
              isDisabled ? "text-slate-600" : "text-slate-200"
            }`}
          >
            #{player.jersey_number}
          </span>
          <span
            data-testid={`player-name-${player.id}`}
            className={`flex-1 text-xs sm:text-sm truncate ${
              isDisabled ? "text-slate-600" : "text-slate-100"
            }`}
          >
            {formatDisplayName(player.full_name)}
          </span>
          <span
            data-testid={`player-position-${player.id}`}
            className={`text-[9px] uppercase tracking-wider border px-1.5 py-0.5 rounded-full ${meta.badge}`}
          >
            {player.position || meta.label}
          </span>
          {hasYellow && (
            <span
              data-testid={`player-card-status-yellow-${player.id}`}
              className="inline-flex items-center gap-1 rounded-md border border-yellow-400/50 bg-yellow-500/20 px-1.5 py-1"
            >
              {Array.from({
                length: Math.max(1, cardStatus?.yellowCount || 0),
              }).map((_, index) => (
                <span
                  key={`${player.id}-yellow-${index}`}
                  className="h-2.5 w-1.5 rounded-sm bg-yellow-300"
                />
              ))}
            </span>
          )}
          {hasRed && (
            <span
              data-testid={`player-card-status-red-${player.id}`}
              className="inline-flex items-center rounded-md border border-red-400/60 bg-red-500/20 px-1.5 py-1"
            >
              <span className="h-2.5 w-1.5 rounded-sm bg-red-400" />
            </span>
          )}
          {isExpelled && (
            <span
              data-testid={`player-disabled-badge-${player.id}`}
              className="inline-flex items-center rounded-md border border-slate-500/70 bg-slate-700/70 px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-200"
            >
              {t("tacticalField.disabledBadge", "Disabled")}
            </span>
          )}
        </div>
      </button>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      data-testid="card-subject-modal"
    >
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div
          className={`sticky top-0 bg-gradient-to-r ${CARD_HEADER_GRADIENT[cardType]} text-white p-6 rounded-t-lg`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard size={28} />
              <div>
                <h2
                  className="text-2xl font-bold"
                  data-testid="card-subject-modal-heading"
                >
                  {t(CARD_TITLE_KEY[cardType], CARD_TITLE_FALLBACK[cardType])}
                </h2>
                <p className="text-white/80 text-sm">
                  {globalClock} • {t(`period.${period}`, `Period ${period}`)}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-black/20 rounded-lg transition-colors"
              data-testid="card-subject-modal-close"
            >
              <X size={24} />
            </button>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="card-team-home"
              onClick={() => onSelectTeam("home")}
              className={`px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wide border transition-colors ${
                selectedTeam === "home"
                  ? "bg-white/20 text-white border-white/50"
                  : "bg-black/10 text-white/70 border-white/20 hover:bg-black/20"
              }`}
            >
              {t("homeTeam", "Home")}
            </button>
            <button
              type="button"
              data-testid="card-team-away"
              onClick={() => onSelectTeam("away")}
              className={`px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wide border transition-colors ${
                selectedTeam === "away"
                  ? "bg-white/20 text-white border-white/50"
                  : "bg-black/10 text-white/70 border-white/20 hover:bg-black/20"
              }`}
            >
              {t("awayTeam", "Away")}
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <p className="text-sm text-slate-400">
            {t(
              "cardSubjectModal.subtitle",
              "Tap a subject to log the card immediately.",
            )}
          </p>

          <div className="space-y-2">
            <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
              {t("onPitch", "On Pitch")}
            </div>
            <div className="grid gap-2 grid-cols-1 sm:grid-cols-2">
              {onFieldPlayers.map(renderPlayerCard)}
            </div>
          </div>

          {benchPlayers.length > 0 && (
            <div
              className="space-y-2"
              data-testid={`bench-section-${selectedTeam}`}
            >
              <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                {t("substitutes", "Substitutes")}
              </div>
              <div className="grid gap-2 grid-cols-1 sm:grid-cols-2">
                {benchPlayers.map(renderPlayerCard)}
              </div>
            </div>
          )}

          {staff.length > 0 && (
            <div
              className="space-y-2"
              data-testid={`staff-section-${selectedTeam}`}
            >
              <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                {t("staffGroupTitle", "Coaching Staff")}
              </div>
              <div className="grid gap-2 grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                {staff.map((member) => {
                  const cardStatus =
                    disciplinaryStatusByPlayer?.[member.staff_id];
                  const hasYellow = (cardStatus?.yellowCount || 0) > 0;
                  const hasRed = Boolean(cardStatus?.red);
                  const isExpelled =
                    expelledPlayerIds?.has(member.staff_id) ?? false;
                  const isDisabled = isSubjectDisabled(member.staff_id);
                  const roleLabel = staffRoleLabel(t, member.role);
                  const displayName =
                    formatDisplayName(member.name) || roleLabel;
                  return (
                    <button
                      key={member.staff_id}
                      type="button"
                      data-testid={`staff-card-${member.staff_id}`}
                      data-staff-row="true"
                      onClick={() => !isDisabled && onSelectStaff(member)}
                      disabled={isDisabled}
                      className={`w-full max-w-none px-3 py-2 rounded-lg border-l-4 border-slate-500/50 text-left transition-all ${
                        isDisabled
                          ? "bg-slate-900 border-slate-800 opacity-50 cursor-not-allowed"
                          : selectedTeam === "home"
                            ? "bg-red-900/20 border-red-900/50 hover:border-red-500/50 hover:bg-red-900/30"
                            : "bg-blue-900/20 border-blue-900/50 hover:border-blue-500/50 hover:bg-blue-900/30"
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <PlayerAvatar
                          player={{ full_name: displayName }}
                          size={20}
                        />
                        <span
                          data-testid={`staff-role-${member.staff_id}`}
                          className="text-[9px] uppercase tracking-wider border px-1.5 py-0.5 rounded-full bg-slate-500/10 text-slate-200 border-slate-500/40"
                        >
                          {roleLabel}
                        </span>
                        <span
                          data-testid={`staff-name-${member.staff_id}`}
                          className={`flex-1 text-xs sm:text-sm truncate ${
                            isDisabled ? "text-slate-600" : "text-slate-100"
                          }`}
                        >
                          {displayName}
                        </span>
                        {hasYellow && (
                          <span
                            data-testid={`staff-card-status-yellow-${member.staff_id}`}
                            className="inline-flex items-center gap-1 rounded-md border border-yellow-400/50 bg-yellow-500/20 px-1.5 py-1"
                          >
                            {Array.from({
                              length: Math.max(1, cardStatus?.yellowCount || 0),
                            }).map((_, index) => (
                              <span
                                key={`${member.staff_id}-yellow-${index}`}
                                className="h-2.5 w-1.5 rounded-sm bg-yellow-300"
                              />
                            ))}
                          </span>
                        )}
                        {hasRed && (
                          <span
                            data-testid={`staff-card-status-red-${member.staff_id}`}
                            className="inline-flex items-center rounded-md border border-red-400/60 bg-red-500/20 px-1.5 py-1"
                          >
                            <span className="h-2.5 w-1.5 rounded-sm bg-red-400" />
                          </span>
                        )}
                        {isExpelled && (
                          <span
                            data-testid={`staff-disabled-badge-${member.staff_id}`}
                            className="inline-flex items-center rounded-md border border-slate-500/70 bg-slate-700/70 px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-200"
                          >
                            {t("tacticalField.disabledBadge", "Disabled")}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
